/**
 * Safari iOS top chrome (html background + theme-color) — kept in sync with MarketingHeader.
 * Scrolled: solid white header → white chrome.
 * At page top: transparent header over hero → each tab's SVG top color.
 */

import {
  MARKETING_HEADER_SCROLL_THRESHOLD,
  getMarketingHeroHeaderTheme,
} from "./marketingConstants.js";
import {
  MARKETING_HERO_BACKDROPS,
  getMarketingHeroBackdrop,
  getMarketingHeroShellTopColor,
  getMarketingPageScrollTop,
  isMarketingHeroBackdropMobileViewport,
} from "./marketingHeroBackdrops.js";

export const MARKETING_HEADER_CHROME_SCROLLED = "#ffffff";
export const MARKETING_HERO_DOC_THEMES = Object.keys(MARKETING_HERO_BACKDROPS);
const FOOTER_OVERSCROLL_BG = "var(--footer-bg, var(--nm-blue-wash-deep))";

export function isMarketingHeaderAtPageTop(scrollTop = getMarketingPageScrollTop()) {
  return scrollTop <= MARKETING_HEADER_SCROLL_THRESHOLD;
}

/** Resolved header / Safari top chrome color for the current pathname + scroll position. */
export function getMarketingHeaderChromeColor(
  pathname,
  { scrollTop = getMarketingPageScrollTop(), mobile = isMarketingHeroBackdropMobileViewport() } = {},
) {
  const pageTheme = getMarketingHeroHeaderTheme(pathname);
  if (!pageTheme) return MARKETING_HEADER_CHROME_SCROLLED;
  if (!isMarketingHeaderAtPageTop(scrollTop)) return MARKETING_HEADER_CHROME_SCROLLED;

  const heroBackdrop = getMarketingHeroBackdrop(pathname);
  return getMarketingHeroShellTopColor(heroBackdrop, { mobile }) ?? MARKETING_HEADER_CHROME_SCROLLED;
}

let appliedThemeColor = "";
let appliedPathname = "";

function setThemeColorMeta(color, pathname, { force = false } = {}) {
  const pathChanged = pathname !== appliedPathname;
  if (!force && !pathChanged && color === appliedThemeColor) return;

  appliedThemeColor = color || "";
  appliedPathname = pathname;

  document.querySelector('meta[name="theme-color"]')?.remove();

  if (color && !color.startsWith("var(")) {
    const meta = document.createElement("meta");
    meta.setAttribute("name", "theme-color");
    meta.setAttribute("content", color);
    document.head.appendChild(meta);
  }
}

export function clearMarketingHeaderChrome() {
  const html = document.documentElement;
  const body = document.body;
  const root = document.getElementById("marketing-root");

  html.classList.remove(
    "nm-marketing-hero-doc",
    "nm-marketing-hero-doc--at-top",
    "nm-marketing-hero-doc--scrolled",
    ...MARKETING_HERO_DOC_THEMES.map(theme => `nm-marketing-hero-doc--${theme}`),
  );
  body.classList.remove("nm-marketing-hero-doc");
  root?.classList.remove("nm-marketing-hero-doc");

  html.style.removeProperty("--nm-mkt-shell-top");
  html.style.removeProperty("--nm-mkt-shell-top-mobile");
  html.style.removeProperty("--nm-mkt-shell-bottom");
  html.style.removeProperty("--nm-mkt-shell-bottom-mobile");
  html.style.removeProperty("--nm-mkt-header-chrome-bg");
  html.style.removeProperty("background-color");
  body.style.removeProperty("background-color");

  document.querySelector('meta[name="theme-color"]')?.remove();
  appliedThemeColor = "";
  appliedPathname = "";
}

/** Apply html/body overscroll colors + theme-color to match MarketingHeader chrome. */
export function applyMarketingHeaderChrome(pathname, { scrollTop = getMarketingPageScrollTop(), forceTheme = false } = {}) {
  const heroBackdrop = getMarketingHeroBackdrop(pathname);
  const pageTheme = getMarketingHeroHeaderTheme(pathname);

  if (!heroBackdrop || !pageTheme) {
    clearMarketingHeaderChrome();
    return;
  }

  const html = document.documentElement;
  const body = document.body;
  const root = document.getElementById("marketing-root");
  const mobile = isMarketingHeroBackdropMobileViewport();
  const atPageTop = isMarketingHeaderAtPageTop(scrollTop);
  const chromeColor = getMarketingHeaderChromeColor(pathname, { scrollTop, mobile });
  const desktopTop = getMarketingHeroShellTopColor(heroBackdrop, { mobile: false });
  const mobileTop = getMarketingHeroShellTopColor(heroBackdrop, { mobile: true });

  MARKETING_HERO_DOC_THEMES.forEach(theme => {
    html.classList.toggle(`nm-marketing-hero-doc--${theme}`, theme === pageTheme);
  });
  html.classList.add("nm-marketing-hero-doc");
  html.classList.toggle("nm-marketing-hero-doc--at-top", atPageTop);
  html.classList.toggle("nm-marketing-hero-doc--scrolled", !atPageTop);
  body.classList.add("nm-marketing-hero-doc");
  root?.classList.add("nm-marketing-hero-doc");

  html.style.setProperty("--nm-mkt-shell-top", desktopTop ?? "");
  html.style.setProperty("--nm-mkt-shell-top-mobile", mobileTop ?? desktopTop ?? "");
  html.style.setProperty("--nm-mkt-shell-bottom", FOOTER_OVERSCROLL_BG);
  html.style.setProperty("--nm-mkt-shell-bottom-mobile", FOOTER_OVERSCROLL_BG);
  html.style.setProperty("--nm-mkt-header-chrome-bg", chromeColor);
  html.style.setProperty("background-color", chromeColor, "important");
  // iOS Safari often samples body (not html) for the top safe-area / status bar tint.
  body.style.setProperty(
    "background-color",
    atPageTop ? chromeColor : FOOTER_OVERSCROLL_BG,
    "important",
  );

  setThemeColorMeta(chromeColor, pathname, { force: forceTheme });
}

/** Set Safari top chrome before React mounts (see marketing.html inline bootstrap). */
export function bootstrapMarketingHeaderChromeFromLocation() {
  if (typeof window === "undefined") return;
  applyMarketingHeaderChrome(window.location.pathname, { scrollTop: 0, forceTheme: true });
}
